import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import { useTheme, listThemes, type SkinOverride } from '@evi/skins';
import {
  SectionLabel,
  ModeSegment,
  CheckIcon,
  PaletteIcon,
  ClockIcon,
} from './theme-picker/primitives.js';

/**
 * Admin accent presets — parity with the driver app's Midnight / Sunset /
 * Bloom skins. On the driver side these are DB-seeded Unlock docs (skin
 * overlays composing over the Default family, gated behind kWh milestones);
 * the admin surface has no unlock model, so the SAME palettes ship here as
 * hardcoded presets, unconditionally available (matching how admin families
 * are never locked). Values are copied verbatim from the catalogue source
 * of truth, packages/db/src/seeds/unlocks.ts — keep the two in sync when a
 * palette is tuned.
 *
 * Each preset pins themeId 'default' exactly like the seed metadata: the
 * skins are curated against the Default base palette. ThemeProvider keeps
 * the admin's saved family untouched underneath, so clearing the accent
 * restores it.
 */
interface AccentPreset {
  code: string;
  name: string;
  swatches: readonly [string, string, string];
  variants: { light: SkinOverride; dark: SkinOverride };
}

const ACCENT_PRESETS: AccentPreset[] = [
  {
    code: 'midnight',
    name: 'Midnight',
    swatches: ['#3B82F6', '#60A5FA', '#E6EEF9'],
    variants: {
      dark: { themeId: 'default', accentColor: '#3B82F6', chargingColor: '#60A5FA' },
      light: {
        themeId: 'default',
        accentColor: '#3B82F6',
        chargingColor: '#60A5FA',
        surface: '#E6EEF9',
        bg: '#F0F5FC',
      },
    },
  },
  {
    code: 'sunset',
    name: 'Sunset',
    swatches: ['#F97316', '#FB923C', '#FDBA74'],
    variants: {
      light: { themeId: 'default', accentColor: '#F97316', chargingColor: '#FB923C' },
      dark: { themeId: 'default', accentColor: '#F97316', chargingColor: '#FB923C' },
    },
  },
  {
    code: 'bloom',
    name: 'Bloom',
    swatches: ['#F59E0B', '#FCD34D', '#FFF8E7'],
    variants: {
      light: {
        themeId: 'default',
        accentColor: '#F59E0B',
        chargingColor: '#FCD34D',
        surface: '#FFF8E7',
        bg: '#FFFCF2',
        border: '#F4D77A',
        textMute: '#B8860B',
      },
      dark: {
        themeId: 'default',
        accentColor: '#FB923C',
        chargingColor: '#FDE047',
        surface: '#2D1F0B',
        bg: '#1F1407',
        border: '#A16207',
        textMute: '#D97706',
      },
    },
  },
];

/** ThemeProvider deliberately never persists skin overrides (the driver's
 *  equivalent persists server-side via active_skin_code); the admin accent
 *  keeps its own key so a reload re-applies it. */
const ACCENT_STORAGE_KEY = 'evi.admin.accent';

function readStoredAccent(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACCENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeAccent(code: string | null): void {
  try {
    if (code) window.localStorage.setItem(ACCENT_STORAGE_KEY, code);
    else window.localStorage.removeItem(ACCENT_STORAGE_KEY);
  } catch {
    // Private mode / quota — in-memory apply still holds for this tab.
  }
}

export interface ThemePickerProps {
  className?: string;
}

/**
 * Admin theme-picker popover. Surfaces two independent axes:
 *
 *   1. MODE (Light / Dark) — segmented control at the top. Flips the
 *      active variant of the currently-selected family.
 *   2. THEME — a grid of compact tiles, one per registered family.
 *      Clicking a tile switches family while keeping the current mode.
 *
 * Both axes persist independently through `ThemeProvider` so reloading
 * preserves `(theme, mode)`.
 *
 * The driver PWA's picker is a separate component (`DriverThemePicker`)
 * because it layers recency + unlock gating on top of this list. The
 * Mode segment is shared via `./theme-picker/primitives.tsx`; the theme
 * tiles are local to this file (`ThemeTile`), so the driver's ThemeRow
 * list stays visually unchanged.
 */
export function ThemePicker({ className }: ThemePickerProps): JSX.Element {
  const { theme, mode, setThemeId, setMode, setSkinOverride } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const themes = listThemes();
  // Active accent preset (Midnight / Sunset / Bloom), re-applied on boot
  // from localStorage. This picker mounts in the admin TopNav on every
  // authed page, so the mount effect below doubles as the boot re-apply.
  const [accent, setAccent] = useState<string | null>(readStoredAccent);

  // Apply the accent for the CURRENT mode — and re-apply when the mode
  // flips, because the light/dark variants differ (Midnight-light and
  // Bloom carry surface/bg shifts the other mode must not keep).
  useEffect(() => {
    const preset = ACCENT_PRESETS.find((p) => p.code === accent) ?? null;
    if (!preset) {
      setSkinOverride(null);
      return;
    }
    setSkinOverride({ ...preset.variants[mode === 'light' ? 'light' : 'dark'] });
  }, [accent, mode, setSkinOverride]);

  const pickAccent = (code: string | null): void => {
    setAccent(code);
    storeAccent(code);
  };
  // Mobile detection so the popover can swap a desktop right-anchored
  // panel for a viewport-pinned sheet (see panelStyle below). Matches
  // the admin's 640px mobile token in lib/breakpoints.ts.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 639,
  );

  useEffect(() => {
    if (!open) return;
    function onDown(e: Event): void {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    function onResize(): void {
      setIsMobile(window.innerWidth <= 639);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const panelBase: CSSProperties = {
    background: 'var(--evi-surface)',
    border: '1px solid var(--evi-border)',
    borderRadius: 12,
    padding: 8,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)',
    zIndex: 50,
  };
  // Desktop: anchor under the button at the panel's natural width.
  // Mobile: pin to the viewport via fixed plus both gutters so the panel
  // never clips off either edge, regardless of where the theme button
  // sits in the right rail. Mirrors the driver PWA's mobile popover. The
  // nav is 60px tall below 1024px, so the sheet drops just beneath it,
  // capped at the desktop width and centered for the wider mobile range.
  const panelStyle: CSSProperties = isMobile
    ? {
        ...panelBase,
        position: 'fixed',
        top: 68,
        left: 12,
        right: 12,
        width: 'auto',
        maxWidth: 460,
        marginInline: 'auto',
      }
    : {
        ...panelBase,
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        width: 460,
      };

  return (
    <div
      ref={rootRef}
      className={className}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        aria-label="Themes"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: open ? 'var(--evi-surface-2)' : 'var(--evi-surface)',
          border: `1px solid ${open ? 'var(--evi-accent)' : 'var(--evi-border)'}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
          color: 'var(--evi-text)',
          transition: 'background 0.2s ease, border-color 0.2s ease',
        }}
      >
        <PaletteIcon />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Themes"
          style={panelStyle}
        >
          <SectionLabel>Mode</SectionLabel>
          <ModeSegment value={mode} onChange={setMode} allowed={theme.availableModes} />

          <SectionLabel style={{ marginTop: 12 }}>Theme</SectionLabel>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: 6,
            }}
          >
            {themes.map((t) => (
              <ThemeTile
                key={t.id}
                name={t.name}
                swatches={t.swatches}
                // With an accent applied the effective family is Default (the
                // presets pin themeId), so tiles reflect the accent-free
                // choice being made when one is clicked.
                active={accent === null && t.id === theme.id}
                onSelect={() => {
                  // A family choice replaces any accent — otherwise the pinned
                  // Default base would make family tiles appear dead.
                  pickAccent(null);
                  setThemeId(t.id);
                }}
              />
            ))}
          </div>

          <SectionLabel style={{ marginTop: 12 }}>Accent</SectionLabel>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: 6,
            }}
          >
            <ThemeTile
              name="None"
              swatches={['#9CA3AF', '#6B7280', '#4B5563']}
              active={accent === null}
              onSelect={() => pickAccent(null)}
            />
            {ACCENT_PRESETS.map((p) => (
              <ThemeTile
                key={p.code}
                name={p.name}
                swatches={p.swatches}
                active={accent === p.code}
                onSelect={() => pickAccent(p.code)}
              />
            ))}
          </div>

          <div
            style={{
              padding: '10px',
              marginTop: 4,
              borderTop: '1px solid var(--evi-border)',
              fontSize: 11,
              color: 'var(--evi-text-mute)',
              display: 'flex',
              gap: 6,
              alignItems: 'center',
            }}
          >
            <ClockIcon /> More themes coming soon
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact theme tile for the admin picker's grid. Local to this file —
 * the driver picker keeps the full-width `ThemeRow` from primitives, so
 * the two pickers diverge here, not in the shared primitive. Shows the
 * family name (single line, ellipsised), its three swatches, and an
 * active check. Click switches the family. Visual language mirrors
 * `ThemeRow` (same swatch + check + hover treatment) at tile scale.
 */
function ThemeTile({
  name,
  swatches,
  active,
  onSelect,
}: {
  name: string;
  swatches: readonly [string, string, string];
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onSelect}
      onMouseEnter={(e) => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = 'var(--evi-surface-2)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 10,
        minWidth: 0,
        borderRadius: 8,
        border: `1px solid ${active ? 'var(--evi-accent)' : 'var(--evi-border)'}`,
        background: active ? 'var(--evi-surface-2)' : 'transparent',
        color: 'var(--evi-text)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        transition: 'background 0.15s ease, border-color 0.15s ease',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 6,
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {name}
        </span>
        <span
          aria-hidden
          style={{
            width: 14,
            height: 14,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--evi-accent)',
            visibility: active ? 'visible' : 'hidden',
          }}
        >
          <CheckIcon />
        </span>
      </div>
      <div style={{ display: 'inline-flex', gap: 4 }}>
        {swatches.map((hex, i) => (
          <span
            key={i}
            aria-hidden
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: hex,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
        ))}
      </div>
    </button>
  );
}
